import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert, Box, Button, Card, CardContent, Checkbox, FormControl,
    Grid, IconButton, InputAdornment, InputLabel, Select, Snackbar,
    TextField, Tooltip, Typography
} from "@mui/material";
import {
    Clear as IconClear,
    Search as IconSearch
} from "@mui/icons-material";

import MantenimientoForm from "../../components/common/MantenimientoForm";
import TrabajadorSearch from "../../components/TrabajadorSearch";
import * as catalogoService from "../../services/catalogoService";
import { getUsuario, addUpdateUsuario, addUpdateUsuarioRoles, getFilialesList } from "../../services/usuarioService";
import { obtenerUsuario } from "../../services/authService";
import { getsRol } from "../../services/seguridadService";
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Alta y edicion de un usuario: codigo, trabajador, clave, estado y roles.
//
// El usuario debe ser un trabajador registrado en el sistema.
//
// Solo la cuenta ADMIN puede crear o modificar usuarios: los demas ven el
// formulario en solo lectura.
const ESTADO_ACTIVO = 2;
const ESTADO_INACTIVO = 3;
const CODIGO_ADMIN = "ADMIN";

const Edit = () => {
    const { isReadOnly: isReadOnlyPermiso } = useMantenimiento("usuario");
    const params = useParams();
    const navigate = useNavigate();

    // Unicamente el ADMIN administra usuarios.
    const esAdmin = String(obtenerUsuario() || "").toUpperCase() === CODIGO_ADMIN;
    const isReadOnly = isReadOnlyPermiso || !esAdmin;

    const codigo = decodeURIComponent(params.id || "");

    const [entity, setEntity] = useState({
        codigo: "",
        idPersona: 0,
        nombres: "",
        numeroDocumento: "",
        clave: "",
        fechaVencimientoClave: null,
        idEstado: ESTADO_ACTIVO,
        claveCargada: false,
        idFilial: 0,
    });

    const [roles, setRoles] = useState([]);          // catalogo de roles
    const [rolesUsuario, setRolesUsuario] = useState([]);  // roles del usuario
    const [listEstado, setListEstado] = useState([]);
    const [listFilial, setListFilial] = useState([]);

    // Sale de la ruta, no de un estado: al guardar un usuario nuevo se navega a
    // /security/user/edit/CODIGO y React reutiliza este componente, asi que un
    // estado se quedaria en "nuevo" (titulo y codigo editable incluidos).
    const nuevo = codigo == "" || codigo == "0";
    const [cargando, setCargando] = useState(true);

    const [openMessage, setOpenMessage] = useState(false);
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    // Modal de búsqueda de trabajadores.
    const [buscandoTrabajador, setBuscandoTrabajador] = useState(false);

    const fetchEntity = useCallback(async () => {
        if (nuevo) {
            setCargando(false);
            return;
        }

        setCargando(true);

        const data = await getUsuario(codigo).catch((err) => { console.log("Err: ", err) });

        if (data) {
            setEntity({
                codigo: data.codigo,
                idPersona: data.idPersona || 0,
                nombres: data.nombres || "",
                numeroDocumento: data.numeroDocumento || "",
                clave: "",
                claveCargada: data.claveCargada,
                fechaVencimientoClave: data.fechaVencimientoClave,
                idEstado: data.idEstado,
                idFilial: data.idFilial || 0,
            });
            // El API devuelve tambien los roles quitados (IdEstado 3): solo se
            // marcan los activos. Si no, al volver a guardar se reactivarian.
            setRolesUsuario((data.roles || []).filter(x => x.idEstado == ESTADO_ACTIVO));
        }

        setCargando(false);
    }, [codigo, nuevo]);

    const fetchListRoles = useCallback(async () => {
        const data = await getsRol().catch((err) => { console.log("Err: ", err) });
        setRoles(data || []);
    }, []);

    const fetchListEstado = useCallback(async () => {
        const data = await catalogoService.getCatalogosByPadre(1)
            .catch((err) => { console.log("Err: ", err) });
        setListEstado(data || []);
    }, []);

    const fetchListFilial = useCallback(async () => {
        const data = await getFilialesList().catch((err) => { console.log("Err: ", err) });
        setListFilial(data || []);
    }, []);

    useEffect(() => {
        fetchEntity();
        fetchListRoles();
        fetchListEstado();
        fetchListFilial();
    }, [codigo]);

    const seleccionarTrabajador = (trabajador) => {
        if (trabajador) {
            setEntity((prev) => ({
                ...prev,
                idPersona: trabajador.idPersona || trabajador.id || 0,
                nombres: trabajador.nombres || "",
                numeroDocumento: trabajador.numeroDocumento || "",
                idFilial: prev.idFilial > 0 ? prev.idFilial : (trabajador.idFilial || 0),
            }));
        }
        setBuscandoTrabajador(false);
    };

    const guardar = async () => {
        // Solo el ADMIN guarda usuarios.
        if (!esAdmin) {
            setPropsMessage({ severity: "error", message: "Solo el usuario ADMIN puede crear o modificar usuarios." });
            setOpenMessage(true);
            return;
        }

        if (entity.codigo.trim() == "") {
            setPropsMessage({ severity: "error", message: "Indique el codigo del usuario." });
            setOpenMessage(true);
            return;
        }

        if (entity.clave != "" && entity.clave.length < 8) {
            setPropsMessage({ severity: "error", message: "La clave debe tener al menos 8 caracteres." });
            setOpenMessage(true);
            return;
        }

        let mensajeError = null;

        const codigoUsuario = await addUpdateUsuario({
            codigo: String(entity.codigo).trim().toUpperCase(),
            idPersona: entity.idPersona || 0,
            clave: entity.clave == null ? "" : entity.clave,
            idEstado: entity.idEstado || ESTADO_ACTIVO,
            idFilial: entity.idFilial || 0,
        }).catch((err) => {
            console.log("Err: ", err);
            mensajeError = err.response?.data?.message || err.response?.data?.detail || err.response?.data?.Detail || err.message;
        });

        if (!codigoUsuario) {
            setPropsMessage({ severity: "error", message: mensajeError || "No se pudo guardar el usuario." });
            setOpenMessage(true);
            return;
        }

        let rolesGuardados = true;

        await addUpdateUsuarioRoles(codigoUsuario, roles.map((rol) => ({
            idRol: rol.id,
            idEstado: rolesUsuario.some(x => x.idRol == rol.id) ? ESTADO_ACTIVO : ESTADO_INACTIVO,
        }))).catch((err) => {
            console.log("Err: ", err);
            rolesGuardados = false;
            mensajeError = err.response?.data?.message || err.response?.data?.detail || err.response?.data?.Detail || err.message;
        });

        if (!rolesGuardados) {
            setPropsMessage({ severity: "error", message: "Se guardo el usuario, pero no sus roles: " + (mensajeError || "error al guardar los roles.") });
            setOpenMessage(true);
            if (nuevo) navigate("/security/user/edit/" + encodeURIComponent(codigoUsuario));
            return;
        }

        setPropsMessage({ severity: "success", message: "Se guardaron los datos del usuario con exito." });
        setOpenMessage(true);

        if (nuevo) navigate("/security/user/edit/" + encodeURIComponent(codigoUsuario));
    };

    const alternarRol = (idRol) => {
        setRolesUsuario((prev) => {
            const existe = prev.some(x => x.idRol == idRol);
            if (existe) return prev.filter(x => x.idRol != idRol);
            return [...prev, { idRol: idRol, codigo: entity.codigo, idEstado: ESTADO_ACTIVO }];
        });
    };

    return (
        <div>
            <MantenimientoForm
                title={(nuevo ? "Nuevo" : "Editar") + " Usuario"}
                backTo={"/security/user"}
                onSave={guardar}
                disabled={isReadOnly}
            >
                {!esAdmin && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Solo el usuario ADMIN puede crear o modificar usuarios. Este formulario es de solo lectura.
                    </Alert>
                )}
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "0px 10px 10px 10px !important" }}>
                                <Grid container>
                                    <Grid item>
                                        <Box sx={{ fontWeight: 'bold' }} padding={0}>
                                            <Typography variant="h7">
                                                Datos Básicos
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            disabled={!nuevo || !esAdmin}
                                            name="codigo"
                                            label="Código"
                                            value={entity.codigo}
                                            fullWidth
                                            onChange={(event) => setEntity({ ...entity, codigo: event.target.value })}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <TextField
                                            disabled
                                            name="trabajador"
                                            label="Trabajador"
                                            value={entity.idPersona == 0 ? "" : (entity.nombres || "")}
                                            fullWidth
                                            size="small"
                                            placeholder="Buscar trabajador..."
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        {entity.idPersona > 0 && !isReadOnly && (
                                                            <Tooltip title="Quitar trabajador" arrow>
                                                                <span>
                                                                    <IconButton
                                                                        aria-label="quitar trabajador"
                                                                        size="small"
                                                                        onClick={() => setEntity((prev) => ({
                                                                            ...prev,
                                                                            idPersona: 0,
                                                                            nombres: "",
                                                                            numeroDocumento: ""
                                                                        }))}
                                                                    >
                                                                        <IconClear fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title="Buscar Trabajador" arrow>
                                                            <span>
                                                                <IconButton
                                                                    aria-label="buscar trabajador"
                                                                    size="small"
                                                                    edge="end"
                                                                    disabled={isReadOnly}
                                                                    onClick={() => setBuscandoTrabajador(true)}
                                                                >
                                                                    <IconSearch fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <FormControl fullWidth>
                                            <InputLabel htmlFor="input-filial">Filial</InputLabel>
                                            <Select
                                                native
                                                label="Filial"
                                                value={entity.idFilial || 0}
                                                disabled={isReadOnly}
                                                onChange={(event) => setEntity({ ...entity, idFilial: parseInt(event.target.value) })}
                                                inputProps={{ name: 'idFilial', id: 'input-filial' }}
                                                size="small"
                                            >
                                                <option key={0} value={0}>{"(Todos)"}</option>
                                                {listFilial.map((row) => (
                                                    <option key={row.id} value={row.id}>{row.descripcion}</option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <FormControl fullWidth>
                                            <InputLabel htmlFor="input-estadoedit">Estado</InputLabel>
                                            <Select
                                                native
                                                label="Estado"
                                                value={entity.idEstado}
                                                disabled={isReadOnly}
                                                onChange={(event) => setEntity({ ...entity, idEstado: parseInt(event.target.value) })}
                                                inputProps={{ name: 'idEstado', id: 'input-estadoedit' }}
                                                size="small"
                                            >
                                                {listEstado.map((row) => (
                                                    <option key={row.id} value={row.id}>{row.descripcion}</option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            name="clave"
                                            label={entity.claveCargada ? "Clave nueva (opcional)" : "Clave"}
                                            type="password"
                                            value={entity.clave}
                                            fullWidth
                                            disabled={isReadOnly}
                                            onChange={(event) => setEntity({ ...entity, clave: event.target.value })}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "0px 10px 10px 10px !important" }}>
                                <Grid container>
                                    <Grid item>
                                        <Box sx={{ fontWeight: 'bold' }} padding={0}>
                                            <Typography variant="h7">
                                                {"Roles del usuario"}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                                    {roles.map((rol) => (
                                        <Grid item xs={12} sm={4} key={rol.id}>
                                            <Checkbox
                                                checked={rolesUsuario.some(x => x.idRol == rol.id)}
                                                disabled={isReadOnly}
                                                onChange={() => alternarRol(rol.id)}
                                            />
                                            <Typography variant="body2" sx={{ display: 'inline' }}>
                                                {rol.nombre + " - " + (rol.descripcion || "")}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </MantenimientoForm>

            {/* Busqueda del trabajador del usuario (ventana estandar) */}
            <TrabajadorSearch
                open={buscandoTrabajador}
                openClose={() => setBuscandoTrabajador(false)}
                setSelection={seleccionarTrabajador}
            />

            <Snackbar open={openMessage} autoHideDuration={5000} onClose={() => setOpenMessage(!openMessage)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={() => setOpenMessage(!openMessage)} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default Edit;
